/**
 * LeaveCalendarView — Story 106-5 (E-106 HR UX Max)
 *
 * Változások a 106-5 scope-ban:
 *  - Employee név feloldás (useEmployees) a UUID helyett
 *  - Status color-coded request row-k (PENDING sárga, APPROVED zöld, REJECTED piros strikethrough)
 *  - Inline expand action: BOLTVEZETO+ role-ban PENDING request row expand-jén
 *    Jóváhagyás / Elutasítás gomb (NEM popover — @kgc/ui-ben nincs, inline cleaner)
 *  - Filter strip: leaveType multi-select chip + employee dropdown + "Csak PENDING" toggle
 *  - Conflict warning badge: napi grid cellán ha >=2 APPROVED ugyanazon a napon
 *  - Optimistic mutation + invalidate + overlap error UX
 */

import { useApiError } from '@/hooks/use-api-error';
import { useAuthStore } from '@/stores/auth-store';
import { useTranslation } from '@kgc/i18n';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from '@kgc/ui';
import { useMemo, useState } from 'react';
import type { LeaveRequest, LeaveTypeCode } from '../api/hr-api';
import { useApproveLeave } from '../hooks/useApproveLeave';
import { useEmployees } from '../hooks/useEmployees';
import { useRejectLeave } from '../hooks/useRejectLeave';

const BOLTVEZETO_ROLES = new Set([
  'BOLTVEZETO',
  'PARTNER_OWNER',
  'CENTRAL_ADMIN',
  'ADMIN',
  'OWNER',
  'DEVOPS_ADMIN',
  'SUPER_ADMIN',
]);

const STATUS_BADGE_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  APPROVED: 'default',
  PENDING: 'secondary',
  REJECTED: 'destructive',
  CANCELLED: 'outline',
};

export const LEAVE_TYPE_CODES: LeaveTypeCode[] = [
  'ANNUAL',
  'SICK_SELF',
  'SICK_CHILD',
  'UNPAID',
  'MATERNITY',
  'PATERNITY',
  'BEREAVEMENT',
  'BLOOD_DONATION',
];

/**
 * SCREAMING_SNAKE kód → a locale-fájlok camelCase kulcsa.
 *
 * A `hr.leave.types.*` kulcsok camelCase-ek (`sickSelf`, `bloodDonation`), a korábbi
 * `code.toLowerCase().replace('_', '')` viszont mindent kisbetűsített → a többszavas
 * kódoknál (`SICK_SELF`, `SICK_CHILD`, `BLOOD_DONATION`) a lookup elvétett, és a `t()`
 * a NYERS KULCSOT renderelte a szűrő-legördülőben, mind a 4 nyelven. A hiba a
 * transzformációban volt, nem a locale-oldalon (types-kulcs 8/8 mind a 4 nyelven).
 *
 * A `/g` flag szándékos: a `replace('_', '')` csak az ELSŐ aláhúzást cserélte, így egy
 * jövőbeli 3-szavas kód (`A_B_C`) is elvétené a kulcsot.
 */
export const toLeaveTypeKey = (code: string): string =>
  code.toLowerCase().replace(/_(\w)/g, (_, ch: string) => ch.toUpperCase());

interface LeaveCalendarViewProps {
  year: number;
  month: number;
  requests: LeaveRequest[];
}

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

function formatMonthName(year: number, month: number): string {
  return new Date(year, month - 1, 1).toLocaleDateString('hu-HU', {
    year: 'numeric',
    month: 'long',
  });
}

/**
 * A frontend NEM ismeri a leaveTypeCode-ot a LeaveRequest-ben (backend csak leaveTypeId-t ad).
 * Ez a helper a backend által visszaadott meta-ból szeparál — ha később bővül a LeaveRequest
 * response-ban a leaveType.code include, ezt a mapper-t kell módosítani.
 * Jelenleg csak "minden típust mutat" filter van frontend-en (és backend fallback-kel).
 */

export function LeaveCalendarView({ year, month, requests }: LeaveCalendarViewProps) {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const daysInMonth = getDaysInMonth(year, month);
  const dayNumbers = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const user = useAuthStore(s => s.user);
  const isBoltvezeto = user !== null && BOLTVEZETO_ROLES.has(user.role);

  const { data: employees = [] } = useEmployees();
  const employeeMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const emp of employees) {
      map.set(emp.id, emp.userName ?? emp.userEmail ?? emp.userId);
    }
    return map;
  }, [employees]);

  const approveMutation = useApproveLeave();
  const rejectMutation = useRejectLeave();

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [leaveTypeFilter, setLeaveTypeFilter] = useState<string>('all');
  const [employeeFilter, setEmployeeFilter] = useState<string>('all');
  const [onlyPending, setOnlyPending] = useState(false);

  const filteredRequests = useMemo(() => {
    return requests.filter(req => {
      if (onlyPending && req.status !== 'PENDING') return false;
      if (employeeFilter !== 'all' && req.employeeProfileId !== employeeFilter) return false;
      if (leaveTypeFilter !== 'all' && req.leaveTypeId !== leaveTypeFilter) return false;
      return true;
    });
  }, [requests, onlyPending, employeeFilter, leaveTypeFilter]);

  // Napi grid: mely kérelmek fedik le a napot
  function requestsForDay(day: number): LeaveRequest[] {
    const date = new Date(year, month - 1, day);
    return filteredRequests.filter(req => {
      const start = new Date(req.startDate);
      const end = new Date(req.endDate);
      return date >= start && date <= end;
    });
  }

  const isWeekend = (day: number): boolean => {
    const d = new Date(year, month - 1, day).getDay();
    return d === 0 || d === 6;
  };

  const handleApprove = async (id: string) => {
    try {
      await approveMutation.mutateAsync(id);
      toast.success(t('hr.leave.approved'));
      setExpandedId(null);
    } catch (err) {
      // 49Y: code-alapú lokalizált toast (LEAVE_OVERLAP / LEAVE_REQUEST_NOT_FOUND / LEAVE_NOT_PENDING
      // — #895 BE) a getErrorMessage-en át; fallback approveError. Kiváltja a string-matchet.
      toast.error(getErrorMessage(err, t('hr.leave.approveError')));
    }
  };

  const handleReject = async (id: string) => {
    try {
      await rejectMutation.mutateAsync(id);
      toast.success(t('hr.leave.rejected'));
      setExpandedId(null);
    } catch (err) {
      // 49Y: code-alapú lokalizált toast (LEAVE_REQUEST_NOT_FOUND / LEAVE_NOT_PENDING — #895 BE);
      // fallback rejectError. Az addig üres catch generikus üzenetét diszkriminatívra cseréli.
      toast.error(getErrorMessage(err, t('hr.leave.rejectError')));
    }
  };

  const isPending = approveMutation.isPending || rejectMutation.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {t('hr.leave.calendar')} — {formatMonthName(year, month)}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Filter strip */}
        <div className="mb-4 flex flex-wrap gap-3 items-center">
          <Select value={leaveTypeFilter} onValueChange={setLeaveTypeFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder={t('hr.leave.filterByType')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('hr.leave.allTypes')}</SelectItem>
              {LEAVE_TYPE_CODES.map(code => (
                <SelectItem key={code} value={code}>
                  {t(`hr.leave.types.${toLeaveTypeKey(code)}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={employeeFilter} onValueChange={setEmployeeFilter}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder={t('hr.leave.filterByEmployee')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('hr.leave.allEmployees')}</SelectItem>
              {employees.map(emp => (
                <SelectItem key={emp.id} value={emp.id}>
                  {emp.userName ?? emp.userEmail ?? emp.userId}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={onlyPending}
              onChange={e => setOnlyPending(e.target.checked)}
              className="rounded"
            />
            {t('hr.leave.onlyPending')}
          </label>
        </div>

        {/* Lista nézet */}
        {filteredRequests.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">{t('hr.leave.noRequests')}</p>
        ) : (
          <div className="space-y-2 mb-6">
            {filteredRequests.map(req => {
              const isExpanded = expandedId === req.id;
              const canAct = isBoltvezeto && req.status === 'PENDING';
              const bgClass =
                req.status === 'APPROVED'
                  ? 'border-green-500/50 bg-green-500/5'
                  : req.status === 'PENDING'
                    ? 'border-yellow-500/50 bg-yellow-500/5'
                    : req.status === 'REJECTED'
                      ? 'border-red-500/50 bg-red-500/5'
                      : 'bg-muted/20';
              return (
                <div key={req.id} className={`rounded-lg border ${bgClass}`}>
                  <button
                    type="button"
                    onClick={() => setExpandedId(isExpanded ? null : req.id)}
                    className={`flex w-full items-center justify-between px-4 py-2 ${
                      canAct ? 'cursor-pointer hover:bg-muted/40' : 'cursor-default'
                    }`}
                    disabled={!canAct}
                    aria-expanded={isExpanded}
                  >
                    <div className="flex flex-col text-left">
                      <span className="text-sm font-medium">
                        {employeeMap.get(req.employeeProfileId) ?? req.employeeProfileId}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(req.startDate).toLocaleDateString('hu-HU')} –{' '}
                        {new Date(req.endDate).toLocaleDateString('hu-HU')} ({req.workingDays}{' '}
                        {t('hr.leave.workingDays')}){req.reason ? ` · ${req.reason}` : ''}
                      </span>
                    </div>
                    <Badge
                      variant={STATUS_BADGE_VARIANT[req.status] ?? 'outline'}
                      className={req.status === 'REJECTED' ? 'line-through' : ''}
                    >
                      {t(`hr.leave.${req.status.toLowerCase()}`)}
                    </Badge>
                  </button>

                  {/* Inline expand: approve / reject akciók */}
                  {isExpanded && canAct && (
                    <div className="border-t px-4 py-3 flex gap-2 bg-background/60">
                      <Button
                        size="sm"
                        onClick={() => void handleApprove(req.id)}
                        disabled={isPending}
                      >
                        ✓ {t('hr.leave.approve')}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => void handleReject(req.id)}
                        disabled={isPending}
                      >
                        ✕ {t('hr.leave.reject')}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setExpandedId(null)}
                        disabled={isPending}
                      >
                        {t('common.cancel')}
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Havi grid nézet */}
        <div className="overflow-x-auto">
          <div
            className="grid gap-0.5"
            style={{ gridTemplateColumns: `repeat(${daysInMonth}, minmax(32px, 1fr))` }}
          >
            {dayNumbers.map(day => {
              const dayRequests = requestsForDay(day);
              const approvedCount = dayRequests.filter(r => r.status === 'APPROVED').length;
              const hasConflict = approvedCount >= 2;
              const hasPending = dayRequests.some(r => r.status === 'PENDING');

              return (
                <div
                  key={day}
                  className={`flex flex-col items-center rounded p-1 text-xs min-h-[48px] relative ${
                    isWeekend(day)
                      ? 'bg-muted/30 text-muted-foreground'
                      : approvedCount > 0
                        ? 'bg-green-100 dark:bg-green-900/30'
                        : hasPending
                          ? 'bg-yellow-100 dark:bg-yellow-900/30'
                          : 'bg-background'
                  }`}
                  title={dayRequests
                    .map(r => employeeMap.get(r.employeeProfileId) ?? r.employeeProfileId)
                    .join(', ')}
                >
                  <span className="font-semibold">{day}</span>
                  {dayRequests.length > 0 && (
                    <span className="mt-0.5 text-[10px] text-center leading-tight">
                      {dayRequests.length}
                    </span>
                  )}
                  {hasConflict && (
                    <span
                      className="absolute -top-1 -right-1 inline-flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white"
                      title={t('hr.leave.conflictWarning')}
                      aria-label={t('hr.leave.conflictWarning')}
                    >
                      !
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
